import { z } from 'zod';

import { ZodAmountRequest, ZodCurrency } from '@Common/types/Amount';
import { ZodB2bSavingPlanDeductibility, ZodB2bSavingPlanType } from '@Common/types/b2b/plans';
import { OrderInitiatorValues } from '@Common/types/bo/orders/common';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import {
  ZodB2bRecurringInvestmentFrequency,
  ZodMutualFundOrderAllocations,
  ZodVoluntaryDeductibility,
} from '@Common/types/explore/mutual_fund_order';
import {
  ZodB2bRecurringInvestmentExecutionsNotExecutedReason,
  ZodB2bRecurringInvestmentExecutionsStatus,
  ZodB2bRecurringInvestmentsPaymentMethod,
} from '@Common/types/explore/recurring_investments';
import { ZodB2BPaymentMethod } from '@Common/types/funding/payment_methods';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { endpoint, type GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

import type { BoListQueryBase } from '@Common/types/bo/bo_list_query';

export const B2bRecurringInvestmentStatusValues = ['active', 'deleted'] as const;
export const ZodB2bRecurringInvestmentStatus = z.enum(B2bRecurringInvestmentStatusValues);

export const B2bRecurringInvestmentPayerValues = ['employee', 'company'] as const;
export const ZodB2bRecurringInvestmentPayer = z.enum(B2bRecurringInvestmentPayerValues);

export const ZodGetB2bRecurringInvestmentResponse = z.object({
  id: ZodId,
  b2b_employee_id: ZodId,
  b2b_company_plan_id: ZodId,
  b2b_company_plan_type: ZodB2bSavingPlanType,
  b2b_contract_id: ZodId,
  status: ZodB2bRecurringInvestmentStatus,
  execution_frequency: ZodB2bRecurringInvestmentFrequency,
  user_input_amount: z.number().int(),
  user_currency: ZodCurrency,
  payer: ZodB2bRecurringInvestmentPayer,
  initiator: z.enum(OrderInitiatorValues),
  deductibility: ZodB2bSavingPlanDeductibility.nullable(),
  execution_starting_date: ZodDateTimeString,
  next_execution_at: ZodDateTimeString,
  last_executed_at: ZodDateTimeString.nullable(),
  created_at: z.date(),
  updated_at: z.date(),
});
export type GetB2bRecurringInvestmentResponse = z.TypeOf<
  typeof ZodGetB2bRecurringInvestmentResponse
>;

export const getB2bRecurringInvestmentsSortColumns: readonly (keyof GetB2bRecurringInvestmentResponse)[] =
  ['id', 'status', 'created_at', 'updated_at', 'next_execution_at'] as const;

const ZodGetB2bRecurringInvestmentsFilters = ZodGetB2bRecurringInvestmentResponse.pick({
  id: true,
  b2b_employee_id: true,
  b2b_company_plan_id: true,
  status: true,
}).partial();

export const ZodGetB2bRecurringInvestmentsQuery = buildZodBoListQueryParsed(
  ZodGetB2bRecurringInvestmentsFilters,
  getB2bRecurringInvestmentsSortColumns,
  { range: [0, 100] },
);
export type GetB2bRecurringInvestmentsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetB2bRecurringInvestmentsFilters>,
  (typeof getB2bRecurringInvestmentsSortColumns)[number]
>;

export const ZodGetB2bRecurringInvestmentsResponse = z.array(ZodGetB2bRecurringInvestmentResponse);
export type GetB2bRecurringInvestmentsResponse = z.TypeOf<
  typeof ZodGetB2bRecurringInvestmentsResponse
>;

export const getB2bRecurringInvestmentsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bRecurringInvestmentsQuery,
  responseBody: ZodGetB2bRecurringInvestmentsResponse,
});

export type GetB2bRecurringInvestmentsEndpoint = GetEndpointType<
  typeof getB2bRecurringInvestmentsEndpoint
>;

export const getB2bRecurringInvestmentsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetB2bRecurringInvestmentResponse,
});

export type GetB2bRecurringInvestmentsIdEndpoint = GetEndpointType<
  typeof getB2bRecurringInvestmentsIdEndpoint
>;

export const putB2bRecurringInvestmentsCancelIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: z.object({
    reason: ZodTrimmedString.min(1),
  }),
  responseBody: z.object({
    id: ZodId,
  }),
});

export type PutB2bRecurringInvestmentsCancelIdEndpoint = GetEndpointType<
  typeof putB2bRecurringInvestmentsCancelIdEndpoint
>;

const ZodPostB2bRecurringInvestmentCommonBody = z.object({
  b2b_employee_id: ZodId,
  b2b_company_plan_id: ZodId,
  payment_method: ZodB2BPaymentMethod.extract(['sepa-direct-debit']),
  deductibility: ZodVoluntaryDeductibility.optional(),
  start_date: ZodDateTimeString,
  frequency: ZodB2bRecurringInvestmentFrequency,
  payer: ZodB2bRecurringInvestmentPayer.default('employee'),
});

export const ZodPostB2bRecurringInvestmentBody = z
  .object({
    ...ZodPostB2bRecurringInvestmentCommonBody.shape,
    user_currency_total_value: ZodAmountRequest,
  })
  .or(
    z.object({
      ...ZodPostB2bRecurringInvestmentCommonBody.shape,
      allocations: ZodMutualFundOrderAllocations.min(1),
    }),
  );

export const postB2bRecurringInvestmentsEndpoint = endpoint({
  method: 'POST',
  body: ZodPostB2bRecurringInvestmentBody,
  responseBody: z.object({
    id: ZodId,
  }),
});

export type PostB2bRecurringInvestmentsEndpoint = GetEndpointType<
  typeof postB2bRecurringInvestmentsEndpoint
>;

export const ZodGetB2bRecurringInvestmentExecutionOneResponse = z.object({
  id: ZodId,
  b2b_recurring_investment_id: ZodId,
  status: ZodB2bRecurringInvestmentExecutionsStatus.nullable(),
  payment_method: ZodB2bRecurringInvestmentsPaymentMethod,
  execution_amount_gross: z.number().int(),
  execution_amount_net: z.number().int(),
  filled_execution_percentage: z.number().int(),
  not_executed_reason: ZodB2bRecurringInvestmentExecutionsNotExecutedReason.nullable(),
  user_currency: ZodCurrency,
  execution_start_date: z.date(),
  execution_end_date: z.date().nullable(),
  od_orders_scheduled_at: z.date().nullable(),
  completed_at: z.date().nullable(),
  co_payment_id: ZodId.nullable(),
  correlation_id: z.string().nullable(),
  retry_attempt: z.number().int(),
  od_order_ids: z.array(ZodId),
  fully_managed_plan_contribution_ids: z.array(ZodId),
  created_at: z.date(),
  updated_at: z.date(),
});

export type GetB2bRecurringInvestmentExecutionOneResponse = z.TypeOf<
  typeof ZodGetB2bRecurringInvestmentExecutionOneResponse
>;

export const ZodGetB2bRecurringInvestmentExecutionsResponse = z.array(
  ZodGetB2bRecurringInvestmentExecutionOneResponse,
);

export type GetB2bRecurringInvestmentExecutionsResponse = z.TypeOf<
  typeof ZodGetB2bRecurringInvestmentExecutionsResponse
>;

export const getB2bRecurringInvestmentExecutionsSortColumns: readonly (keyof GetB2bRecurringInvestmentExecutionOneResponse)[] =
  ['id', 'status', 'execution_start_date', 'created_at', 'updated_at'] as const;

const ZodGetB2bRecurringInvestmentExecutionsFilters =
  ZodGetB2bRecurringInvestmentExecutionOneResponse.pick({
    b2b_recurring_investment_id: true,
  }).partial();

export const ZodGetB2bRecurringInvestmentExecutionsQuery = buildZodBoListQueryParsed(
  ZodGetB2bRecurringInvestmentExecutionsFilters,
  getB2bRecurringInvestmentExecutionsSortColumns,
  { range: [0, 100] },
);

export type GetB2bRecurringInvestmentExecutionsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetB2bRecurringInvestmentExecutionsFilters>,
  (typeof getB2bRecurringInvestmentExecutionsSortColumns)[number]
>;

export const getB2bRecurringInvestmentExecutionsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bRecurringInvestmentExecutionsQuery,
  responseBody: ZodGetB2bRecurringInvestmentExecutionsResponse,
});

export type GetB2bRecurringInvestmentExecutionsEndpoint = GetEndpointType<
  typeof getB2bRecurringInvestmentExecutionsEndpoint
>;

export const B2bRecurringInvestmentAllocationSourceValues = ['allocation', 'managed_grid'] as const;

export const ZodB2bRecurringInvestmentAllocationSource = z.enum(
  B2bRecurringInvestmentAllocationSourceValues,
);

export const ZodGetB2bRecurringInvestmentAllocationOneResponse = z.object({
  id: ZodId,
  b2b_recurring_investment_id: ZodId,
  source: ZodB2bRecurringInvestmentAllocationSource,
  instrument_symbol: ZodTrimmedString.nullable(),
  instrument_name: ZodTrimmedString.nullable(),
  allocation_percentage: z.number(),
  /**
   * Grid rows carry percentages only, a managed plan splits a single total at execution time
   */
  allocation_amount: z.number().int().nullable(),
  user_currency: ZodCurrency,
  /**
   * The grid a row belongs to, null for a libre allocation
   */
  b2b_fully_managed_allocation_id: ZodId.nullable(),
  /**
   * The investor profile the grid was built from, null for a libre allocation
   */
  b2b_fully_managed_allocation_template_id: ZodId.nullable(),
  created_at: z.date(),
  updated_at: z.date(),
});

export type GetB2bRecurringInvestmentAllocationOneResponse = z.TypeOf<
  typeof ZodGetB2bRecurringInvestmentAllocationOneResponse
>;

export const ZodGetB2bRecurringInvestmentAllocationsResponse = z.array(
  ZodGetB2bRecurringInvestmentAllocationOneResponse,
);

export type GetB2bRecurringInvestmentAllocationsResponse = z.TypeOf<
  typeof ZodGetB2bRecurringInvestmentAllocationsResponse
>;

export const getB2bRecurringInvestmentAllocationsSortColumns: readonly (keyof GetB2bRecurringInvestmentAllocationOneResponse)[] =
  ['id', 'allocation_percentage', 'created_at'] as const;

const ZodGetB2bRecurringInvestmentAllocationsFilters =
  ZodGetB2bRecurringInvestmentAllocationOneResponse.pick({
    b2b_recurring_investment_id: true,
  }).partial();

export const ZodGetB2bRecurringInvestmentAllocationsQuery = buildZodBoListQueryParsed(
  ZodGetB2bRecurringInvestmentAllocationsFilters,
  getB2bRecurringInvestmentAllocationsSortColumns,
  { range: [0, 100] },
);
export type GetB2bRecurringInvestmentAllocationsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetB2bRecurringInvestmentAllocationsFilters>,
  (typeof getB2bRecurringInvestmentAllocationsSortColumns)[number]
>;

export const getB2bRecurringInvestmentAllocationsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetB2bRecurringInvestmentAllocationsQuery,
  responseBody: ZodGetB2bRecurringInvestmentAllocationsResponse,
});

export type GetB2bRecurringInvestmentAllocationsEndpoint = GetEndpointType<
  typeof getB2bRecurringInvestmentAllocationsEndpoint
>;
